import { forwardRef, useState } from 'react'
import styled from 'styled-components'
import { ScaleLoader } from 'react-spinners'
import { FaCircle } from 'react-icons/fa'
import { useMainContext } from '../../core/contexts/main'
import { useReadAllNotificationsMutation } from '../../core/services/react-query/useNotificationsQuery'
import { formatDate } from '../../core/utils/common'
import { useWindowSize } from '../../core/hooks/useWindowSize'
import { Link } from 'react-router-dom'
import Text from '../../core/utils/Text'
import Card from '../common/Card'
import { Heading } from '../../styles/newStyles/Dashboard.styled'
import { DText } from '../../styles/CommonStyles'
import { MdPushPin } from 'react-icons/md'
import ReactMarkdown from 'react-markdown'

const SUBTABS = [
	{
		key: 'info',
		tid: 'info-notification',
		title: 'recent-info-notif',
		href: '/notifications/info',
	},
	{ key: 'all', tid: 'notifications', title: 'notifications', href: '/notifications' },
]

export const NotificationDropdown = forwardRef(({ notifications, loading }, ref) => {
	const { width } = useWindowSize()
	const {
		main: { lang },
	} = useMainContext()
	const { mutate: readAllNotifications } = useReadAllNotificationsMutation()

	const [activeTabIndex, setActiveTabIndex] = useState(0)
	const activeTabKey = SUBTABS[activeTabIndex].key

	const infoNotifications = notifications?.data?.filter((n) => n.pinned) || []
	const allNotifications = notifications?.data || []

	return (
		<Wrapper ref={ref}>
			<div className='rounded-xl shadow-sm dark:bg-[#1B1C20] bg-[#fff] p-3'>
				{/* Tabs */}
				<div
					className={
						'dark:bg-dark bg-white  shadow-md !rounded-lg h-[65px] overflow-x-auto overflow-y-hidden w-full flex gap-5 px-5'
					}
				>
					{SUBTABS.map((tab, index) => (
						<button
							key={tab.key}
							onClick={() => setActiveTabIndex(index)}
							className={
								'relative cursor-pointer h-full flex gap-2 items-center justify-center hover:bg-slate-500/10 rounded-md px-3'
							}
						>
							<Text
								tid={tab.tid}
								className={`whitespace-nowrap text-sm ${
									activeTabIndex === index ? 'dark:text-blue-200 text-[#804BEB]' : ''
								}`}
							/>
							{activeTabIndex === index ? (
								<div
									className={
										'absolute bottom-[-8px] left-0 h-[12px] w-full bg-[#804BEB] rounded-xl'
									}
									style={{ boxShadow: '-2px 0px 8.6px 0px #804BEB' }}
								></div>
							) : null}
						</button>
					))}
				</div>

				{/* Tab Content */}
				<div className='mt-3'>
					{loading && (
						<div className='flex items-center justify-center py-4'>
							<ScaleLoader height={18} width={2} color='#804BEB' />
						</div>
					)}

					{!loading && activeTabKey === 'info' && (
						<InfoTabContent data={infoNotifications} lang={lang} />
					)}

					{!loading && activeTabKey === 'all' && (
						<AllTabContent data={allNotifications} lang={lang} />
					)}
				</div>

				<div className='mt-3 flex items-center justify-center p-3'>
					<Link to={SUBTABS[activeTabIndex].href}>
						<Text tid={'see-all'} className='text-cBlue underline' />
					</Link>
				</div>
			</div>
		</Wrapper>
	)
})

const InfoTabContent = ({ data, lang, onClick }) => {
	if (data.length === 0) {
		return (
			<div className='flex items-center justify-center text-sm py-5'>
				<Text tid='no-notification' />
			</div>
		)
	}

	return data.map((notif) => (
		<ItemRow key={notif._id} onClick={onClick}>
			<div className='flex flex-col items-start w-full gap-3'>
				<div className='flex items-center justify-start gap-1 md:gap-4 w-full'>
					<DText className='!text-base !font-semibold'>{notif.title}</DText>
					<DText className='opacity-75 mr-auto md:mx-2'>
						{formatDate(notif.createdAt, 'time', lang === 'en' ? 'en-US' : 'fa-IR')} -{' '}
						{formatDate(notif.createdAt, 'date', lang === 'en' ? 'en-US' : 'fa-IR')}
					</DText>
					<span className='mr-auto hidden md:inline'>
						<MdPushPin size={18} />
					</span>
				</div>
				<div className='w-full text-right'>
					<DText className='opacity-75 mx-2'>
						<ReactMarkdown children={notif.body} />
					</DText>
				</div>
			</div>
		</ItemRow>
	))
}

const AllTabContent = ({ data, lang, onClick }) => {
	if (data.length === 0) {
		return (
			<div className='flex items-center justify-center text-sm py-5'>
				<Text tid='no-notification' />
			</div>
		)
	}

	return data.map((notif) => (
		<ItemRow key={notif._id} onClick={() => onClick()}>
			<Heading className='flex-col lg:flex-row items-start lg:items-center gap-y-2 lg:gap-y-0'>
				<div className='text-sm flex items-center gap-x-2'>
					<FaCircle color='#804BEB' size={8} />
					<h4 className='font-medium dark:text-pColor'>{notif.title}</h4>
				</div>
				<div className='text-xs dark:text-white/50'>
					<span>
						{formatDate(notif.createdAt, 'time', lang === 'en' ? 'en-US' : 'fa-IR', {
							hour: '2-digit',
							minute: '2-digit',
						})}
					</span>
					<span> - </span>
					<span>{formatDate(notif.createdAt, 'date', lang === 'en' ? 'en-US' : 'fa-IR')}</span>
				</div>
			</Heading>
		</ItemRow>
	))
}

const Wrapper = styled.div`
	position: absolute;
	top: calc(100% + 0.5rem);
	left: 0;
	width: 35%;

	@media screen and (max-width: 1024px) {
		width: 60%;
		left: 1rem;
	}
	@media screen and (max-width: 768px) {
		width: 94%;
		left: 1rem;
	}
`

const ItemRow = styled.div`
	transition: all 0.3s;
	padding: 12px;
	color: ${(props) => (props.header && props.header.secondary) || props.theme.color};
	background-color: ${(props) => props.header && props.header.bg_second};
	text-align: ${(props) => props.textAlign};
	font-weight: ${(props) => props.header && 600};
	font-size: ${(props) => props.header && '0.95rem'};
	&:nth-child(even) {
		background-color: ${(props) => props.theme.tableRowBg};
	}
	&:nth-child(odd) {
		background-color: ${(props) => props.theme.surfaceBg};
	}
	border-radius: 8px;
`
